/**
 * The name that goes with the From address.
 *
 * The address is chosen in the composer's From select. The name beside it used to be worked out
 * in four places -- the select's own label, the send payload, the draft save, and the draft
 * created by the first attachment upload -- and they disagreed. A draft opened with one sender
 * and then switched to another kept the first sender's name on screen while sending under a
 * different one.
 *
 * One answer, derived from the selected address, called by all four.
 */

import { extractEmailAddress } from './emailAddressUtils';

export interface SenderIdentity {
  readonly firstName?: string;
  readonly lastName?: string;
}

export interface TeamSender {
  readonly email: string;
  readonly displayName: string;
}

export interface SystemSignatureIdentity extends SenderIdentity {
  readonly id: string;
  /** The mailbox the signature belongs to. A signature only names mail sent from that address. */
  readonly email?: string;
}

/**
 * The sender a draft was saved with. Its name only applies while its own address is the one
 * selected -- switching the From address is how an adjuster sends as someone else.
 */
export interface DraftSender {
  readonly senderEmail?: string;
  readonly senderName?: string;
  readonly senderFirstName?: string;
  readonly senderLastName?: string;
}

export interface SenderNameInput {
  readonly selectedFromEmail: string;
  readonly selectedSignatureId?: string | null;
  readonly teamSenders?: readonly TeamSender[];
  readonly systemSignatures?: readonly SystemSignatureIdentity[];
  /** Whoever owns the selected From address, as the signatures endpoint reports them. */
  readonly fromEmailUserInfo?: SenderIdentity | null;
  /** The signed-in user, used only when nothing else identifies the address. */
  readonly user?: SenderIdentity | null;
  /** Only for a message whose `emailDirection` is `DRAFT`. */
  readonly draft?: DraftSender | null;
}

/**
 * `Name <email>` or a bare address, reduced to the address for comparison.
 *
 * Case-folded on top of `extractEmailAddress`, because these values are compared against each
 * other rather than displayed -- a mailbox typed with different capitalisation is the same mailbox.
 */
export const extractSenderAddress = (value?: string): string =>
  extractEmailAddress(value ?? '').toLowerCase();

const fullName = (identity?: SenderIdentity | null): string =>
  `${identity?.firstName ?? ''} ${identity?.lastName ?? ''}`.trim();

/** True when both values name the same mailbox, in either `Name <email>` or bare form. */
export const isSameSenderAddress = (a?: string, b?: string): boolean => {
  const left = extractSenderAddress(a);
  return left !== '' && left === extractSenderAddress(b);
};

const draftSenderName = (
  draft: DraftSender | null | undefined,
  selectedFromEmail: string
): string => {
  if (!draft || !isSameSenderAddress(draft.senderEmail, selectedFromEmail)) {
    return '';
  }

  return (
    fullName({
      firstName: draft.senderFirstName,
      lastName: draft.senderLastName,
    }) || (draft.senderName ?? '').trim()
  );
};

/**
 * In order: the draft's own sender while its address is still selected, the selected system
 * signature while it belongs to that address, the shared mailbox the address belongs to, whoever
 * owns the address, and -- only when nothing else names it -- the signed-in user.
 *
 * Every step is gated on the selected address. Anything carried over from a previous selection --
 * a draft's sender, a signature left in the body -- stops applying the moment the address changes.
 *
 * Each step takes both halves of a name from a single identity; mixing them produced names that
 * belonged to nobody.
 */
export const resolveSenderName = ({
  selectedFromEmail,
  selectedSignatureId,
  teamSenders = [],
  systemSignatures = [],
  fromEmailUserInfo,
  user,
  draft,
}: SenderNameInput): string => {
  const fromDraft = draftSenderName(draft, selectedFromEmail);
  if (fromDraft) return fromDraft;

  /*
   * Matched on the mailbox as well as the id. The composer keeps a draft's embedded signature id
   * across a From change -- the effect that re-picks a signature returns early for drafts -- so an
   * id alone can name a mailbox the mail is no longer from.
   */
  const systemSignature = selectedSignatureId
    ? systemSignatures.find(
        (signature) =>
          signature.id === selectedSignatureId &&
          isSameSenderAddress(signature.email, selectedFromEmail)
      )
    : undefined;
  const signatureName = fullName(systemSignature);
  if (signatureName) return signatureName;

  const teamSender = teamSenders.find((sender) =>
    isSameSenderAddress(sender.email, selectedFromEmail)
  );
  if (teamSender?.displayName) return teamSender.displayName;

  return fullName(fromEmailUserInfo) || fullName(user);
};
